// src/pages/administrasi/AdmCategoryPage.jsx
//
// One generic page component for every Sekretaris/Bendahara category that's
// backed by the template-schema engine (Form Pengajuan, Surat Masuk, Surat
// Keluar, Pengajuan Dana, Kas Masuk, Kas Keluar, LPJ). The route decides
// which category this is via props — the page itself just renders whatever
// fields the active template(s) for that category define. Arsip Dokumen and
// Anggaran are NOT rendered by this component (see ArsipDokumen.jsx /
// Anggaran.jsx) since they aren't template-driven documents.
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../utils/config";
import { responseErrorMessage } from "../../utils/apiError";
import CustomSelect from "../../components/CustomSelect";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(await responseErrorMessage(res));
  return res.status === 204 ? null : res.json();
}

function StatusBadge({ status }) {
  const map = {
    GENERATED: "bg-green-500/15 text-green-700 dark:text-green-400",
    DRAFT: "bg-gray-400/15 text-gray-600",
    FAILED: "bg-red-500/15 text-red-700 dark:text-red-400",
    MISSING: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  };
  const label = {
    GENERATED: "Berhasil",
    DRAFT: "Draft",
    FAILED: "Gagal",
    MISSING: "File hilang di Drive",
  }[status] || status;
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] || ""}`}>{label}</span>;
}

export default function AdmCategoryPage({ module, category, title, description }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // per-row "Cek" (check-file) state, keyed by submission id
  const [checkingId, setCheckingId] = useState(null);

  // events + templates for this category load once (per category/module)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [evData, tmplData] = await Promise.all([
          apiFetch("/api/administrasi/events/?active_only=1"),
          apiFetch(`/api/administrasi/templates/?module=${module}&category=${category}`),
        ]);
        if (cancelled) return;
        const evList = Array.isArray(evData) ? evData : evData.results || [];
        setEvents(evList);
        setTemplates(Array.isArray(tmplData) ? tmplData : tmplData.results || []);
        if (evList.length > 0) setEventId(String(evList[0].id));
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [module, category]);

  const loadSubmissions = useCallback(async () => {
    if (!eventId) {
      setSubmissions([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(
        `/api/administrasi/submissions/?event_id=${eventId}&category=${category}`
      );
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId, category]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // On-demand check against Drive for one row — never automatic, so just
  // viewing this list never itself burns Drive API quota. Always gives an
  // explicit confirmation either way, not just a (missable) badge-color
  // change, so it's obvious the check actually ran.
  const handleCheckFile = async (submissionId) => {
    setCheckingId(submissionId);
    try {
      const updated = await apiFetch(`/api/administrasi/submissions/${submissionId}/check-file/`, {
        method: "POST",
      });
      setSubmissions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      // "unseen" is not "deleted": the app's Drive account cannot see this file
      // (typically uploaded through another Google account), so it is left as
      // it was rather than flagged missing.
      alert(
        {
          ok: "File masih ada di Drive.",
          trashed: "File ini sudah dihapus (ada di tempat sampah Drive).",
          unseen:
            "File ini tidak terlihat oleh akun Drive yang dipakai aplikasi — kemungkinan diunggah lewat akun Google lain. Belum tentu terhapus, jadi tidak ditandai hilang; buka lewat tombol Buka untuk memastikan.",
        }[updated.check] || "Pengecekan selesai."
      );
    } catch (e) {
      alert(e.message);
    } finally {
      setCheckingId(null);
    }
  };

  // Client-side filter — matches against the template name, who submitted
  // it, and the submitted field values themselves (so searching for e.g. a
  // nomor surat or a name typed into the form finds it too).
  const filteredSubmissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((s) => {
      const haystack = [
        s.template_name,
        s.submitted_by_username,
        ...Object.values(s.data || {}),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [submissions, search]);

  const selectedTemplate = templates.find((t) => String(t.id) === String(selectedTemplateId));

  const openForm = () => {
    setFormError("");
    setSelectedTemplateId(templates.length === 1 ? String(templates[0].id) : "");
    setFieldValues({});
    setShowForm(true);
  };

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    setFieldValues({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!eventId) {
      setFormError("Pilih event terlebih dahulu.");
      return;
    }
    if (!selectedTemplate) {
      setFormError("Pilih jenis dokumen.");
      return;
    }
    setSaving(true);
    try {
      const result = await apiFetch("/api/administrasi/submissions/", {
        method: "POST",
        body: JSON.stringify({
          event_id: Number(eventId),
          template_id: Number(selectedTemplateId),
          data: fieldValues,
        }),
      });
      setShowForm(false);
      loadSubmissions();
      if (result?.drive_file_url) window.open(result.drive_file_url, "_blank");
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {description && <p className="text-sm text-muted mt-1">{description}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={loadSubmissions}
            disabled={loading}
            title="Perbarui daftar (mis. setelah file dihapus/diubah langsung di Drive)"
            className="text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
          >
            {loading ? "Memuat…" : "Refresh"}
          </button>
          <button
            onClick={openForm}
            disabled={!eventId || templates.length === 0}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
          >
            + Buat Baru
          </button>
        </div>
      </div>

      <div className="glass-card p-4 mb-4">
        <label className="block text-xs font-medium mb-1">Event</label>
        <CustomSelect
          value={eventId}
          onChange={setEventId}
          options={events.map((ev) => ({ value: String(ev.id), label: ev.name }))}
          placeholder="-- Pilih event --"
        />
        {events.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">
            Belum ada event aktif.{" "}
            <Link to="/administrasi/folder-config" className="underline">
              Mulai event
            </Link>
            .
          </p>
        )}
        {events.length > 0 && templates.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">
            Belum ada template untuk kategori ini. Tambahkan lewat menu{" "}
            <Link to="/administrasi/template-manager" className="underline">
              Template Surat
            </Link>
            .
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      <div className="mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari jenis dokumen, pengaju, atau isi field…"
          className="w-full max-w-sm rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
        />
      </div>

      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table text-sm">
          <thead>
            <tr>
              <th className="text-left">Tanggal</th>
              <th className="text-left">Jenis</th>
              <th className="text-left">Status</th>
              <th className="text-left">Diajukan oleh</th>
              <th className="text-right">Dokumen</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubmissions.map((s) => (
              <tr key={s.id} className="border-t">
                <td className="px-3 py-2">{new Date(s.created_at).toLocaleString("id-ID")}</td>
                <td className="px-3 py-2">{s.template_name}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={s.status} />
                </td>
                <td className="px-3 py-2">{s.submitted_by_username || "-"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {s.drive_file_url ? (
                      <a
                        href={s.drive_file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-600 hover:underline"
                      >
                        Buka
                      </a>
                    ) : s.status === "FAILED" ? (
                      <span className="text-red-600 text-xs" title={s.error_message}>
                        Gagal
                      </span>
                    ) : (
                      "-"
                    )}
                    {(s.status === "GENERATED" || s.status === "MISSING") && (
                      <button
                        type="button"
                        onClick={() => handleCheckFile(s.id)}
                        disabled={checkingId === s.id}
                        title="Cek apakah file ini masih ada di Drive"
                        className="text-xs text-muted hover:text-brand-600 disabled:opacity-50"
                      >
                        {checkingId === s.id ? "Mengecek…" : "Cek"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && submissions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted">
                  Belum ada data.
                </td>
              </tr>
            )}
            {!loading && submissions.length > 0 && filteredSubmissions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted">
                  Tidak ada yang cocok dengan pencarian.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Buat {title}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {templates.length > 1 && (
                <div>
                  <label className="block text-xs font-medium mb-1">Jenis Dokumen</label>
                  <CustomSelect
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    options={templates.map((t) => ({ value: String(t.id), label: t.name }))}
                    placeholder="-- Pilih jenis --"
                  />
                </div>
              )}

              {selectedTemplate &&
                (selectedTemplate.fields || []).map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium mb-1">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </label>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={3}
                        value={fieldValues[f.key] || ""}
                        onChange={(e) =>
                          setFieldValues((p) => ({ ...p, [f.key]: e.target.value }))
                        }
                        className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                      />
                    ) : (
                      <input
                        type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                        value={fieldValues[f.key] || ""}
                        onChange={(e) =>
                          setFieldValues((p) => ({ ...p, [f.key]: e.target.value }))
                        }
                        className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                      />
                    )}
                  </div>
                ))}

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving || !selectedTemplate}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Membuat..." : "Buat & Unggah ke Drive"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
